import { gql, useQuery } from '@apollo/client';
import { useStatus as getStatus } from 'hooks/useStatus';
import { useWorkspace } from 'hooks/useWorkspace';
import { Reporter } from 'interfaces/Reporter';
import { Status } from 'interfaces/Status';
import { WorkOrder } from 'interfaces/WorkOrder';
import { useMemo, useState } from 'react';
import { generateColor } from 'utils/generateColor';
interface PerformanceMetric {
  value: number;
  label: string;
  percentChange?: number;
}

export interface ResponseTimeMetric {
  current: {
    value: string;
    unit: string;
    label: string;
  };
  previous: {
    value: string;
    unit: string;
    label: string;
  };
}

interface PieChartMetric {
  value: number;
  label: string;
  color: string;
}

type Resident = Pick<Reporter, 'id' | 'name' | 'email' | 'phone'> & {
  count: number;
  rank: number;
};

interface Module<TData> {
  data: TData;
  isLoading: boolean;
}

interface List<TData> {
  visibleItems: TData[];
  showMore: () => void;
  showLess: () => void;
  hasMore: boolean;
  isExpanded: boolean;
  isLoading: boolean;
}

interface PerformanceMetrics {
  performanceMetrics: Module<{
    residentSubmissions: PerformanceMetric;
    completedWorkOrders: PerformanceMetric;
    deniedWorkOrders: PerformanceMetric;
    canceledWorkOrders: PerformanceMetric;
    residentsEngaged: PerformanceMetric;
  }>;
  responseTimes: Module<{
    medianTimeToStart: ResponseTimeMetric;
    medianTimeToComplete: ResponseTimeMetric;
  }>;
  workOrdersByStatus: Module<PieChartMetric[]>;
  workOrdersByCategory: Module<PieChartMetric[]>;
  mostEngagedResidents: List<Resident>;
  unassignedWorkOrders: List<WorkOrder>;
  workOrdersOnHold: List<WorkOrder>;
  outstandingWorkOrders: List<WorkOrder>;
}

const WORK_ORDER_CARD_FIELDS = gql`
  fragment WorkOrderCardFields on WorkOrder {
    workorderNumber
    title
    location {
      address
    }
    status
    assignee {
      id
    }
    createdAt
  }
`;

const GET_PERFORMANCE_METRICS = gql`
  query PerformanceMetrics($organizationId: ID!, $createdFrom: Float!, $createdTo: Float!) {
    organization(id: $organizationId) {
      performance(params: { createdFrom: $createdFrom, createdTo: $createdTo }) {
        residents {
          nodes {
            name
            email
            phone
            rank
            count
          }
        }
        residentEngagement {
          totalResidents
          totalSubmissions
        }
        responseTimes {
          averageTimeToComplete
        }
      }
    }
  }
`;

const GET_MEDIAN_RESPONSE_TIMES = gql`
  query MedianResponseTimes($organizationId: ID!, $createdFrom: Float!, $createdTo: Float!) {
    organization(id: $organizationId) {
      performance(params: { createdFrom: $createdFrom, createdTo: $createdTo }) {
        responseTimes {
          medianTimeToStart
          medianTimeToComplete
        }
      }
    }
  }
`;

const GET_WORK_ORDERS_BY_STATUS = gql`
  query WorkOrdersByStatus($organizationId: ID!, $createdFrom: Float!, $createdTo: Float!) {
    organization(id: $organizationId) {
      workorders(params: { createdFrom: $createdFrom, createdTo: $createdTo }) {
        edges {
          node {
            id
            ...WorkOrderCardFields
          }
        }
        aggs {
          statuses {
            nodes {
              status
              count
            }
          }
          categories {
            nodes {
              category {
                id
                name
              }
              count
            }
          }
        }
      }
    }
  }
  ${WORK_ORDER_CARD_FIELDS}
`;

const GET_WORK_ORDERS_ON_HOLD = gql`
  query WorkOrdersOnHold($organizationId: ID!) {
    organization(id: $organizationId) {
      workorders(params: { statuses: "${Status.OnHold}" }) {
        nodes {
          id
          ...WorkOrderCardFields
        }
      }
    }
  }
  ${WORK_ORDER_CARD_FIELDS}
`;

const GET_OUTSTANDING_WORK_ORDERS = gql`
  query OutstandingWorkOrders($organizationId: ID!, $createdFrom: Float!, $createdTo: Float!) {
    organization(id: $organizationId) {
      workorders(params: { createdFrom: $createdFrom, createdTo: $createdTo, statuses: "${Status.Submitted},${Status.Started}" }) {
        nodes {
          id
          ...WorkOrderCardFields
        }
      }
    }
  }
  ${WORK_ORDER_CARD_FIELDS}
`;

const hexToRgba = (hex: string, alpha: number = 1): string => {
  const cleanHex = hex.replace('#', '');

  const expandedHex =
    cleanHex.length === 3
      ? cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2]
      : cleanHex;

  const r = parseInt(expandedHex.substring(0, 2), 16);
  const g = parseInt(expandedHex.substring(2, 4), 16);
  const b = parseInt(expandedHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatTimeFromMs = (ms: number): { value: string; unit: string } => {
  if (!ms || ms <= 0) {
    return { value: '0', unit: 'minutes' };
  }

  const hours = ms / (1000 * 60 * 60);

  if (hours >= 24) {
    const days = Math.round(hours / 24);
    return { value: days.toString(), unit: days === 1 ? 'day' : 'days' };
  }

  if (hours >= 1) {
    const roundedHours = Math.round(hours);
    return { value: roundedHours.toString(), unit: roundedHours === 1 ? 'hour' : 'hours' };
  }

  const minutes = Math.round(ms / (1000 * 60));
  return { value: minutes.toString(), unit: minutes === 1 ? 'minute' : 'minutes' };
};

const formatDateRangeLabel = (fromMs: number, toMs: number): string => {
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const from = new Date(fromMs).toLocaleDateString('en-US', options);
  const to = new Date(toMs).toLocaleDateString('en-US', options);
  return `${from} – ${to}`;
};

const calculatePercentChange = (current: number, previous: number): number => {
  if (previous === 0) {
    return current > 0 ? 100 : 0;
  }
  return Math.round(((current - previous) / previous) * 100);
};

const usePerformanceMetrics = (): PerformanceMetrics => {
  const {
    workspace: { organizationId },
  } = useWorkspace();

  const [outstandingItemsCount, setOutstandingItemsCount] = useState(5);
  const [onHoldItemsCount, setOnHoldItemsCount] = useState(5);
  const [unassignedItemsCount, setUnassignedItemsCount] = useState(5);
  const [residentItemsCount, setResidentItemsCount] = useState(5);

  const {
    today,
    startOfMonth,
    startOfPrevMonth,
    thisTimeLastMonth,
    last7Days,
    lastYear,
    past30DaysStart,
    prev30DaysStart,
  } = useMemo(() => {
    const now = new Date();
    const nowMs = now.getTime();
    const DAY_MS = 24 * 60 * 60 * 1000;
    return {
      today: nowMs,
      startOfMonth: new Date(now.getFullYear(), now.getMonth(), 1).getTime(),
      startOfPrevMonth: new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime(),
      thisTimeLastMonth: new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).getTime(),
      last7Days: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).getTime(),
      lastYear: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime(),
      past30DaysStart: nowMs - 30 * DAY_MS,
      prev30DaysStart: nowMs - 60 * DAY_MS,
    };
  }, []);

  const getPrevPerformanceMetrics = useQuery(GET_PERFORMANCE_METRICS, {
    variables: { organizationId, createdFrom: startOfPrevMonth, createdTo: thisTimeLastMonth },
  });

  const getPerformanceMetrics = useQuery(GET_PERFORMANCE_METRICS, {
    variables: { organizationId, createdFrom: startOfMonth, createdTo: today },
  });

  const getCurrentMedianResponseTimes = useQuery(GET_MEDIAN_RESPONSE_TIMES, {
    variables: { organizationId, createdFrom: past30DaysStart, createdTo: today },
  });

  const getPrevMedianResponseTimes = useQuery(GET_MEDIAN_RESPONSE_TIMES, {
    variables: { organizationId, createdFrom: prev30DaysStart, createdTo: past30DaysStart },
  });

  const getPrevWorkOrdersByStatus = useQuery(GET_WORK_ORDERS_BY_STATUS, {
    variables: { organizationId, createdFrom: startOfPrevMonth, createdTo: thisTimeLastMonth },
  });

  const getWorkOrdersByStatus = useQuery(GET_WORK_ORDERS_BY_STATUS, {
    variables: { organizationId, createdFrom: startOfMonth, createdTo: today },
  });

  const getWorkOrdersOnHold = useQuery(GET_WORK_ORDERS_ON_HOLD, {
    variables: { organizationId },
  });

  const averageTimeToCompleteDate = useMemo(() => {
    if (getPrevPerformanceMetrics.loading) {
      return undefined;
    }

    const previousCompleteTime =
      getPrevPerformanceMetrics.data?.organization?.performance?.responseTimes
        ?.averageTimeToComplete || 0;

    if (previousCompleteTime > 0) {
      return today - previousCompleteTime;
    }

    return last7Days;
  }, [getPrevPerformanceMetrics.data, getPrevPerformanceMetrics.loading, today, last7Days]);

  const getOutstandingWorkOrders = useQuery(GET_OUTSTANDING_WORK_ORDERS, {
    variables: { organizationId, createdFrom: lastYear, createdTo: averageTimeToCompleteDate },
    skip: !averageTimeToCompleteDate,
  });

  const performanceMetrics = useMemo(() => {
    const prevResidentSubmissions =
      getPrevPerformanceMetrics.data?.organization?.performance?.residentEngagement
        ?.totalSubmissions || 0;
    const currResidentSubmissions =
      getPerformanceMetrics.data?.organization?.performance?.residentEngagement?.totalSubmissions ||
      0;

    const prevCompletedWorkOrders = (
      getPrevWorkOrdersByStatus.data?.organization?.workorders?.edges || []
    )
      .map((edge: { node: WorkOrder }) => edge.node)
      .filter((node: WorkOrder) => node.status === Status.Completed);
    const currCompletedWorkOrders = (
      getWorkOrdersByStatus.data?.organization?.workorders?.edges || []
    )
      .map((edge: { node: WorkOrder }) => edge.node)
      .filter((node: WorkOrder) => node.status === Status.Completed);

    const canceledWorkOrders = (getWorkOrdersByStatus.data?.organization?.workorders?.edges || [])
      .map((edge: { node: WorkOrder }) => edge.node)
      .filter((node: WorkOrder) => node.status === Status.Cancelled);

    const deniedWorkOrders = (getWorkOrdersByStatus.data?.organization?.workorders?.edges || [])
      .map((edge: { node: WorkOrder }) => edge.node)
      .filter((node: WorkOrder) => node.status === Status.Denied);

    const prevResidents =
      getPrevPerformanceMetrics.data?.organization?.performance?.residentEngagement
        ?.totalResidents || 0;
    const currResidents =
      getPerformanceMetrics.data?.organization?.performance?.residentEngagement?.totalResidents ||
      0;

    return {
      data: {
        residentSubmissions: {
          value: currResidentSubmissions,
          label: 'Resident submissions',
          percentChange: calculatePercentChange(currResidentSubmissions, prevResidentSubmissions),
        },
        completedWorkOrders: {
          value: currCompletedWorkOrders.length,
          label: 'Completed work orders',
          percentChange: calculatePercentChange(
            currCompletedWorkOrders.length,
            prevCompletedWorkOrders.length
          ),
        },
        deniedWorkOrders: { value: deniedWorkOrders.length, label: 'Work orders denied' },
        canceledWorkOrders: { value: canceledWorkOrders.length, label: 'Work orders canceled' },
        residentsEngaged: {
          value: currResidents,
          label: 'Residents engaged this month',
          percentChange: calculatePercentChange(currResidents, prevResidents),
        },
      },
      isLoading:
        (getPerformanceMetrics.loading && !getPerformanceMetrics.data) ||
        (getPrevPerformanceMetrics.loading && !getPrevPerformanceMetrics.data) ||
        (getWorkOrdersByStatus.loading && !getWorkOrdersByStatus.data) ||
        (getPrevWorkOrdersByStatus.loading && !getPrevWorkOrdersByStatus.data),
    };
  }, [
    getPrevPerformanceMetrics,
    getPerformanceMetrics,
    getPrevWorkOrdersByStatus,
    getWorkOrdersByStatus,
  ]);

  const responseTimes = useMemo(() => {
    const currentStartTime =
      getCurrentMedianResponseTimes.data?.organization?.performance?.responseTimes
        ?.medianTimeToStart || 0;
    const currentCompleteTime =
      getCurrentMedianResponseTimes.data?.organization?.performance?.responseTimes
        ?.medianTimeToComplete || 0;

    const previousStartTime =
      getPrevMedianResponseTimes.data?.organization?.performance?.responseTimes
        ?.medianTimeToStart || 0;
    const previousCompleteTime =
      getPrevMedianResponseTimes.data?.organization?.performance?.responseTimes
        ?.medianTimeToComplete || 0;

    const currentLabel = formatDateRangeLabel(past30DaysStart, today);
    const previousLabel = formatDateRangeLabel(prev30DaysStart, past30DaysStart);

    return {
      data: {
        medianTimeToStart: {
          current: { ...formatTimeFromMs(currentStartTime), label: currentLabel },
          previous: { ...formatTimeFromMs(previousStartTime), label: previousLabel },
        },
        medianTimeToComplete: {
          current: { ...formatTimeFromMs(currentCompleteTime), label: currentLabel },
          previous: { ...formatTimeFromMs(previousCompleteTime), label: previousLabel },
        },
      },
      isLoading:
        (getCurrentMedianResponseTimes.loading && !getCurrentMedianResponseTimes.data) ||
        (getPrevMedianResponseTimes.loading && !getPrevMedianResponseTimes.data),
    };
  }, [
    getCurrentMedianResponseTimes,
    getPrevMedianResponseTimes,
    past30DaysStart,
    prev30DaysStart,
    today,
  ]);

  const workOrdersByStatus = useMemo(() => {
    const allowedStatuses = [Status.Submitted, Status.Started, Status.OnHold, Status.Reopened];
    const data = (getWorkOrdersByStatus.data?.organization?.workorders?.aggs?.statuses?.nodes || [])
      .filter((node: { status: Status }) => allowedStatuses.includes(node.status))
      .map((node: { status: Status; count: number }) => ({
        value: node.count,
        label: getStatus(node.status).label,
        color: hexToRgba(getStatus(node.status).color || '#D9D9D9', 0.8),
      }));

    return {
      data,
      isLoading: getWorkOrdersByStatus.loading && !getWorkOrdersByStatus.data,
    };
  }, [getWorkOrdersByStatus]);

  const workOrdersByCategory = useMemo(() => {
    const nodes =
      getWorkOrdersByStatus.data?.organization?.workorders?.aggs?.categories?.nodes || [];
    const sortedNodes = [...nodes].sort((a, b) => b.count - a.count);

    const topCategories = sortedNodes.slice(0, 5);
    const otherCategories = sortedNodes.slice(5);

    const result = topCategories.map((node: { category: { name: string }; count: number }) => ({
      value: node.count,
      label: node.category.name,
      color: hexToRgba(generateColor(node.category.name) || '#D9D9D9', 0.8),
    }));

    if (otherCategories.length > 0) {
      const otherCount = otherCategories.reduce((sum, node) => sum + node.count, 0);
      result.push({
        value: otherCount,
        label: 'Other',
        color: '#D9D9D9',
      });
    }

    return {
      data: result,
      isLoading: getWorkOrdersByStatus.loading && !getWorkOrdersByStatus.data,
    };
  }, [getWorkOrdersByStatus]);

  const mostEngagedResidents = useMemo(() => {
    const allItems = getPerformanceMetrics.data?.organization?.performance?.residents?.nodes || [];

    const mappedItems: Resident[] = allItems.map(
      (
        resident: { name: string; email: string; phone: string; count: number; rank: number },
        index: number
      ) => ({
        id: index,
        name: resident.name,
        email: resident.email,
        phone: resident.phone,
        count: resident.count,
        rank: resident.rank,
      })
    );

    const visibleItems = mappedItems.slice(0, residentItemsCount);
    const hasMore = visibleItems.length < mappedItems.length;
    const isExpanded = mappedItems.length > 0 && visibleItems.length === mappedItems.length;

    const showMore = () => {
      setResidentItemsCount((prev) => prev + 5);
    };

    const showLess = () => {
      setResidentItemsCount(5);
    };

    return {
      visibleItems,
      showMore,
      showLess,
      hasMore,
      isExpanded,
    };
  }, [getPerformanceMetrics.data, residentItemsCount]);

  const unassignedWorkOrders = useMemo(() => {
    const allItems = (getWorkOrdersByStatus.data?.organization?.workorders?.edges || [])
      .map((edge: { node: WorkOrder }) => edge.node)
      .filter(
        (node: WorkOrder) =>
          [Status.Submitted, Status.Started, Status.Reopened].includes(node.status) &&
          !node.assignee?.id
      );

    const visibleItems = allItems.slice(0, unassignedItemsCount);
    const hasMore = visibleItems.length < allItems.length;
    const isExpanded = allItems.length > 0 && visibleItems.length === allItems.length;

    const showMore = () => {
      setUnassignedItemsCount((prev) => prev + 5);
    };

    const showLess = () => {
      setUnassignedItemsCount(5);
    };

    return {
      visibleItems,
      showMore,
      showLess,
      hasMore,
      isExpanded,
    };
  }, [getWorkOrdersByStatus.data, unassignedItemsCount]);

  const workOrdersOnHold = useMemo(() => {
    const allItems = getWorkOrdersOnHold.data?.organization?.workorders?.nodes || [];

    const visibleItems = allItems.slice(0, onHoldItemsCount);
    const hasMore = visibleItems.length < allItems.length;
    const isExpanded = allItems.length > 0 && visibleItems.length === allItems.length;

    const showMore = () => {
      setOnHoldItemsCount((prev) => prev + 5);
    };

    const showLess = () => {
      setOnHoldItemsCount(5);
    };

    return {
      visibleItems,
      showMore,
      showLess,
      hasMore,
      isExpanded,
    };
  }, [getWorkOrdersOnHold.data, onHoldItemsCount]);

  const outstandingWorkOrders = useMemo(() => {
    const allItems = getOutstandingWorkOrders.data?.organization?.workorders?.nodes || [];

    const sortedItems = [...allItems].sort((a, b) => a.createdAt - b.createdAt);

    const visibleItems = sortedItems.slice(0, outstandingItemsCount);
    const hasMore = visibleItems.length < sortedItems.length;
    const isExpanded = sortedItems.length > 0 && visibleItems.length === sortedItems.length;

    const showMore = () => {
      setOutstandingItemsCount((prev) => prev + 5);
    };

    const showLess = () => {
      setOutstandingItemsCount(5);
    };

    return {
      visibleItems,
      showMore,
      showLess,
      hasMore,
      isExpanded,
    };
  }, [getOutstandingWorkOrders.data, outstandingItemsCount]);

  return {
    performanceMetrics,
    responseTimes,
    workOrdersByStatus,
    workOrdersByCategory,
    mostEngagedResidents: {
      ...mostEngagedResidents,
      isLoading: getPerformanceMetrics.loading,
    },
    unassignedWorkOrders: {
      ...unassignedWorkOrders,
      isLoading: getWorkOrdersByStatus.loading,
    },
    workOrdersOnHold: {
      ...workOrdersOnHold,
      isLoading: getWorkOrdersOnHold.loading || !getWorkOrdersOnHold.data,
    },
    outstandingWorkOrders: {
      ...outstandingWorkOrders,
      isLoading: getOutstandingWorkOrders.loading || !getOutstandingWorkOrders.data,
    },
  };
};

export default usePerformanceMetrics;
